// ServiceLad paid report body. ReportPage.jsx keeps loading, the header and
// the PDF download; this renders the report itself from slReportModel.js.
// Icons get an explicit colour: the PDF snapshot cannot resolve currentColor
// inside SVGs.
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  Car,
  CheckCircle2,
  ClipboardCheck,
  Fingerprint,
  Gauge,
  HelpCircle,
  History,
  Landmark,
  Lock,
  Receipt,
  ShieldAlert,
  Tag,
  Users,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import Plate from "../../components/Plate";
import { formatUkRegistration } from "../../utils/registration";
import { buildSlReport, formatMiles, formatReportDate } from "./slReportModel.js";

const INK = "#126bdb";
const TONE_INK = { good: "#13804b", bad: "#c4302b", warn: "#b45309", muted: "#64748b" };
const STATUS_LABEL = { good: "Clear", bad: "Attention", warn: "Check", muted: "Not checked" };

function Pill({ tone, label }) {
  return <span className={`slr-pill slr-pill-${tone}`}>{label || STATUS_LABEL[tone]}</span>;
}

function Section({ id, icon: SectionIcon, title, intro, tone, toneLabel, children }) {
  return (
    <section className="slr-section" id={id} data-pdf-block>
      <header className="slr-section-head">
        <span className="slr-section-icon"><SectionIcon size={22} color={INK} strokeWidth={1.9} aria-hidden="true" /></span>
        <div className="slr-section-title">
          <h2>{title}</h2>
          {intro && <p>{intro}</p>}
        </div>
        {tone && <Pill tone={tone} label={toneLabel} />}
      </header>
      {children}
    </section>
  );
}

// Big statement card: green tick, red warning, or grey "not checked".
function Marker({ tone, title, children }) {
  const MarkerIcon = tone === "good" ? CheckCircle2 : tone === "muted" ? HelpCircle : AlertTriangle;
  return (
    <div className={`slr-marker slr-marker-${tone}`} data-pdf-block>
      <MarkerIcon size={34} color={TONE_INK[tone]} strokeWidth={1.8} aria-hidden="true" />
      <div>
        <h3>{title}</h3>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

function Facts({ rows }) {
  const present = rows.filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!present.length) return null;
  return (
    <dl className="slr-facts">
      {present.map(([label, value]) => (
        <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  );
}

/* ---------- Mileage chart (inline SVG, PDF safe) ---------- */

const CHART_W = 760;
const CHART_H = 270;
const PAD = { left: 52, right: 28, top: 34, bottom: 38 };

function niceMax(value) {
  if (value <= 0) return 10000;
  const target = value * 1.08;
  const power = 10 ** Math.floor(Math.log10(target));
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((multiple) => multiple * power >= target);
  return step * power;
}

function shortMiles(value) {
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(value);
}

function MileageChart({ points }) {
  const times = points.map((point) => new Date(point.date).getTime());
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const top = niceMax(Math.max(...points.map((point) => point.miles)));
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;
  const x = (t) => PAD.left + (maxT === minT ? plotW / 2 : ((t - minT) / (maxT - minT)) * plotW);
  const y = (miles) => PAD.top + plotH - (miles / top) * plotH;
  const coords = points.map((point, i) => ({ ...point, cx: x(times[i]), cy: y(point.miles) }));
  const line = coords.map((c, i) => `${i ? "L" : "M"}${c.cx.toFixed(1)},${c.cy.toFixed(1)}`).join(" ");
  const area = `${line} L${coords[coords.length - 1].cx.toFixed(1)},${(PAD.top + plotH).toFixed(1)} L${coords[0].cx.toFixed(1)},${(PAD.top + plotH).toFixed(1)} Z`;
  // Label a point only when it has room; the latest reading always gets one.
  let lastLabelX = -Infinity;
  const labelled = new Set();
  coords.forEach((c, i) => {
    const isLast = i === coords.length - 1;
    if (c.miles === 0 && c.start) return;
    if (isLast || c.dropped || c.cx - lastLabelX > 58) {
      if (isLast && c.cx - lastLabelX < 58) labelled.delete(i - 1);
      labelled.add(i);
      lastLabelX = c.cx;
    }
  });
  const font = "Figtree, Arial, sans-serif";
  return (
    <svg className="slr-chart" viewBox={`0 0 ${CHART_W} ${CHART_H}`} width={CHART_W} height={CHART_H} role="img" aria-label="Mileage recorded at each MOT">
      <defs>
        <linearGradient id="slrArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#126bdb" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#126bdb" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((fraction) => (
        <g key={fraction}>
          <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(top * fraction)} y2={y(top * fraction)} stroke="#dbe3ee" strokeDasharray="4 5" />
          <text x={PAD.left - 10} y={y(top * fraction) + 4} textAnchor="end" fontSize="12" fill="#64748b" fontFamily={font}>{shortMiles(top * fraction)}</text>
        </g>
      ))}
      <path d={area} fill="url(#slrArea)" />
      <path d={line} fill="none" stroke="#126bdb" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" />
      {coords.map((c, i) => (
        <g key={`${c.date}-${i}`}>
          <circle cx={c.cx} cy={c.cy} r={c.dropped ? 7 : 5} fill="#ffffff" stroke={c.dropped ? "#c4302b" : "#126bdb"} strokeWidth={c.dropped ? 4 : 3} />
          {labelled.has(i) && (
            <text x={c.cx} y={c.cy - 13} textAnchor={i === coords.length - 1 ? "end" : "middle"} fontSize="12" fontWeight="700" fill={c.dropped ? "#c4302b" : "#0f172a"} fontFamily={font}>
              {new Intl.NumberFormat("en-GB").format(c.miles)}
            </text>
          )}
        </g>
      ))}
      <text x={PAD.left} y={CHART_H - 10} fontSize="12" fill="#64748b" fontFamily={font}>{formatReportDate(points[0].date)}</text>
      <text x={CHART_W - PAD.right} y={CHART_H - 10} textAnchor="end" fontSize="12" fill="#64748b" fontFamily={font}>{formatReportDate(points[points.length - 1].date)}</text>
    </svg>
  );
}

/* ---------- Timeline icons ---------- */

const KIND_ICON = {
  first: BadgeCheck,
  mot: ClipboardCheck,
  keeper: Users,
  v5c: Receipt,
  plate: Tag,
  finance: Landmark,
  writeoff: ShieldAlert,
  status: AlertTriangle,
  other: History,
};

/* ---------- The report ---------- */

// Timeline rows shown before "See more". Print and the PDF always show all
// of them (`.slr-expand-all`, set by ReportPage while it takes the snapshot).
const TIMELINE_SHOWN = 8;

export default function SlReport({ report, order, vehicle, details }) {
  const [timelineOpen, setTimelineOpen] = useState(false);
  const model = buildSlReport(report, { vehicle });
  const reg = vehicle?.registration || "";
  const displayReg = formatUkRegistration(reg);
  const { mot, mileage, timeline } = model;
  const vehicleLine = [
    details.year,
    vehicle?.colour,
    vehicle?.fuel_type,
    details.engineCc ? `${new Intl.NumberFormat("en-GB").format(details.engineCc)} cc` : null,
    details.transmission,
  ].filter(Boolean).join(" · ");
  const packageLabel = report.checks_ordered?.[0]?.label || "Vehicle history report";

  return (
    <div className="slr">
      {/* ---------- Page one: the summary ---------- */}
      <section className="slr-cover" data-pdf-block data-pdf-break-after>
        <div className="slr-cover-top">
          <picture>
            <source srcSet="/servicelad.webp" type="image/webp" />
            <img className="slr-logo" src="/servicelad.png" alt="ServiceLad" />
          </picture>
          <span className="slr-badge"><ShieldAlert size={16} color={INK} aria-hidden="true" />{packageLabel}</span>
        </div>

        <div className="slr-hero">
          <div className="slr-hero-copy">
            <p className="slr-eyebrow">Vehicle history report · {displayReg}</p>
            <h1>
              {details.makeLogo && <img className="slr-make-logo" src={details.makeLogo} alt="" width="40" height="40" />}
              {details.title}
            </h1>
            {vehicleLine && <p className="slr-vehicle-line">{vehicleLine}</p>}
            {details.vin && <p className="slr-vehicle-line slr-vin">VIN: {details.vin}</p>}
            <p className="slr-generated">
              Generated {formatReportDate(report.generated_at) || "today"}
              {order?.id && <> · Ref {order.id.slice(-12).toUpperCase()}</>}
            </p>
          </div>
          <div className="slr-hero-car">
            {details.imageSrc ? <img src={details.imageSrc} alt={details.title} /> : <div className="slr-hero-car-empty"><Car size={72} color="#9fb6d6" strokeWidth={1.2} aria-hidden="true" /></div>}
            {reg && <Plate registration={reg} />}
          </div>
        </div>

        <div className={`slr-verdict slr-verdict-${model.verdict.tone}`}>
          {model.verdict.tone === "good"
            ? <CheckCircle2 size={40} color={TONE_INK.good} strokeWidth={1.8} aria-hidden="true" />
            : model.verdict.tone === "muted"
              ? <HelpCircle size={40} color={TONE_INK.muted} strokeWidth={1.8} aria-hidden="true" />
              : <AlertTriangle size={40} color={TONE_INK.bad} strokeWidth={1.8} aria-hidden="true" />}
          <div>
            <span className="slr-verdict-kicker">Summary</span>
            <h2>{model.verdict.title}</h2>
            <p>{model.verdict.text}</p>
            {model.alerts.length > 0 && (
              <ul className="slr-alert-list">
                {model.alerts.map((item) => <li key={item.text}><a href={item.href}>{item.text}</a></li>)}
              </ul>
            )}
          </div>
        </div>

        <nav className="slr-checks" aria-label="Key checks">
          {model.quick.map((check) => (
            <a className={`slr-check slr-check-${check.tone}`} href={check.href} key={check.id}>
              <span>{check.label}</span>
              <strong>{check.value}</strong>
            </a>
          ))}
        </nav>

        {model.notes.length > 0 && (
          <p className="slr-notes">
            <strong>Good to know:</strong>{" "}
            {model.notes.map((note, i) => (
              <span key={note.text}>{i > 0 && " · "}<a href={note.href}>{note.text}{note.detail && <span className="slr-note-detail"> {note.detail}</span>}</a></span>
            ))}
          </p>
        )}

        {model.glance.length > 0 && (
          <div className="slr-glance">
            {model.glance.map((item) => (
              <a href={item.href} key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* ---------- Mileage ---------- */}
      {mileage.available && (
        <Section
          id="mileage"
          icon={Gauge}
          title="Mileage history"
          intro="Odometer readings taken at each MOT, oldest first."
          tone={mileage.alert ? "bad" : "good"}
          toneLabel={mileage.alert ? "Attention" : "No drop"}
        >
          {mileage.alert ? (
            <Marker tone="bad" title="A mileage reading went down">
              {mileage.drops.map((drop) => `${formatMiles(drop.miles)} on ${formatReportDate(drop.date)}`).join(", ")} is lower than the reading before it. This can mean the odometer was changed or replaced, or a tester typed the wrong figure. Ask the seller for paperwork.
            </Marker>
          ) : (
            <Marker tone="good" title="No mileage drop found">
              Every MOT reading is higher than the one before it.
            </Marker>
          )}
          <div className="slr-stats">
            <div><span>Last recorded</span><strong>{formatMiles(mileage.last.miles)}</strong><small>{formatReportDate(mileage.last.date)}</small></div>
            <div><span>First MOT reading</span><strong>{formatMiles(mileage.first.miles)}</strong><small>{formatReportDate(mileage.first.date)}</small></div>
            {mileage.perYear && <div><span>Average a year</span><strong>{formatMiles(mileage.perYear)}</strong><small>{mileage.chart[0]?.start ? "Since first registration" : "Between MOT readings"}</small></div>}
          </div>
          <div className="slr-card slr-chart-card" data-pdf-block>
            <MileageChart points={mileage.chart} />
          </div>
        </Section>
      )}

      {/* ---------- Write-off and damage ---------- */}
      {report.risk_checks && (
        <Section
          id="damage"
          icon={ShieldAlert}
          title="Write-off and damage"
          intro="Insurance loss records from the national register."
          tone={!model.known ? "muted" : model.writeoffs.length ? "bad" : "good"}
        >
          {!model.known ? (
            <Marker tone="muted" title="Write-off record not available">
              The provider did not return write-off data, so this check is not shown as clear.
            </Marker>
          ) : model.writeoffs.length === 0 ? (
            <Marker tone="good" title="No write-off recorded">
              No insurance write-off is recorded against this vehicle{model.checkedOn ? ` (checked on ${model.checkedOn})` : ""}.
            </Marker>
          ) : (
            <>
              <Marker tone="bad" title={model.writeoffs.length === 1 ? "Insurance write-off recorded" : `${model.writeoffs.length} insurance write-offs recorded`}>
                Check the repair was done properly before you buy. A written-off vehicle is usually worth less and can cost more to insure.
              </Marker>
              {model.writeoffs.map((entry, i) => (
                <div className="slr-card slr-record" key={i} data-pdf-block>
                  <div className="slr-record-head">
                    <div><span>Write-off {i + 1}</span><h3>{entry.categoryLabel || entry.status || "Write-off recorded"}</h3></div>
                    <Pill tone="bad" label={entry.categoryLetter ? `Cat ${entry.categoryLetter}` : "Recorded"} />
                  </div>
                  <Facts rows={[
                    ["Date of loss", formatReportDate(entry.lossDate)],
                    ["Damage", entry.damageLocations?.length ? entry.damageLocations.join(", ") : null],
                    ["Cause", entry.causeOfDamage],
                    ["Insurer", entry.insurerName],
                    ["Claim number", entry.claimNumber],
                    ["Insurer contact", entry.contactNumber],
                    ["Recorded in", entry.showUkBadge ? "United Kingdom" : entry.country],
                  ]} />
                </div>
              ))}
            </>
          )}
        </Section>
      )}

      {/* ---------- Timeline ---------- */}
      {timeline.all.length > 0 && (
        <Section id="timeline" icon={History} title="Vehicle history timeline" intro="The big moments in this vehicle's life, newest first.">
          {timeline.featured.length > 1 && (
            <div className="slr-moments" data-pdf-block>
              {timeline.featured.map((item) => {
                const KindIcon = KIND_ICON[item.kind] || History;
                return (
                  <div className={`slr-moment slr-moment-${item.tone}`} key={item.id}>
                    <span className="slr-moment-icon"><KindIcon size={24} color={item.tone === "bad" ? TONE_INK.bad : INK} strokeWidth={1.8} aria-hidden="true" /></span>
                    <strong>{item.label}</strong>
                    <em>{formatReportDate(item.date)}</em>
                    {item.detail && <small>{item.detail}</small>}
                  </div>
                );
              })}
            </div>
          )}
          <ol className={`slr-card slr-timeline${timelineOpen ? " slr-timeline-open" : ""}`}>
            {timeline.all.map((item, i) => (
              <li className={`slr-timeline-item slr-tone-${item.tone}${i >= TIMELINE_SHOWN ? " slr-timeline-extra" : ""}`} key={item.id} data-pdf-block>
                <time>{formatReportDate(item.date)}</time>
                <i aria-hidden="true" />
                <div><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</div>
              </li>
            ))}
          </ol>
          {timeline.all.length > TIMELINE_SHOWN && (
            <button type="button" className="slr-more" onClick={() => setTimelineOpen((open) => !open)} aria-expanded={timelineOpen}>
              {timelineOpen ? "See less" : `See more (${timeline.all.length - TIMELINE_SHOWN})`}
            </button>
          )}
        </Section>
      )}

      {/* ---------- Finance ---------- */}
      {report.risk_checks && (
        <Section
          id="finance"
          icon={Landmark}
          title="Finance"
          intro="Agreements recorded against this vehicle, including settled ones."
          tone={!model.known ? "muted" : model.runningFinance.length || (model.financeFound && !model.finance.length) ? "bad" : model.finance.length ? "warn" : "good"}
          toneLabel={!model.known ? undefined : model.runningFinance.length ? "Still running" : model.finance.length ? "Ended" : undefined}
        >
          {!model.known ? (
            <Marker tone="muted" title="Finance record not available">
              The provider did not return finance data, so this check is not shown as clear.
            </Marker>
          ) : model.runningFinance.length || (model.financeFound && !model.finance.length) ? (
            <Marker tone="bad" title="Finance still running">
              The lender may still own this vehicle. Ask the seller for a settlement letter before you pay.
            </Marker>
          ) : model.finance.length === 0 ? (
            <Marker tone="good" title="No finance agreement found">
              No finance is recorded against this vehicle{model.checkedOn ? ` (checked on ${model.checkedOn})` : ""}.
            </Marker>
          ) : null}
          {model.finance.map((entry, i) => (
            <div className="slr-card slr-record" key={i} data-pdf-block>
              <div className="slr-record-head">
                <div><span>Agreement {i + 1}</span><h3>{entry.provider || "Finance agreement"}</h3></div>
                <Pill tone={entry.outstanding ? "bad" : "good"} label={entry.outstanding ? "Still running" : "Term ended"} />
              </div>
              <Facts rows={[
                ["Agreement", entry.agreementType],
                ["Agreement no.", entry.agreementNumber],
                ["Lender phone", entry.contactNumber],
                ["Started", formatReportDate(entry.started)],
                ["Term", entry.term],
                [entry.outstanding ? "Ends" : "Ended", formatReportDate(entry.endsOn)],
              ]} />
            </div>
          ))}
        </Section>
      )}

      {/* ---------- Stolen ---------- */}
      {report.risk_checks && (
        <Section id="stolen" icon={Lock} title="Stolen marker" intro="Police and insurance theft records." tone={!model.known ? "muted" : model.stolen ? "bad" : "good"}>
          {!model.known ? (
            <Marker tone="muted" title="Stolen record not available">The provider did not return theft data, so this check is not shown as clear.</Marker>
          ) : model.stolen ? (
            <Marker tone="bad" title="Recorded as stolen">Do not buy this vehicle. Contact the police on 101 with the registration.</Marker>
          ) : (
            <Marker tone="good" title="No stolen marker found">No theft record is held for this vehicle{model.checkedOn ? ` (checked on ${model.checkedOn})` : ""}.</Marker>
          )}
        </Section>
      )}

      {/* ---------- MOT ---------- */}
      {(mot.tests.length > 0 || mot.status) && (
        <Section id="mot" icon={ClipboardCheck} title="MOT history" intro="Official MOT tests, mileage and recorded faults." tone={mot.status ? (mot.valid ? "good" : "bad") : "muted"} toneLabel={mot.label || undefined}>
          {mot.valid ? (
            <Marker tone="good" title={`MOT valid until ${formatReportDate(mot.expiry)}`}>{mot.countdown ? `${mot.countdown} left.` : null}</Marker>
          ) : mot.status ? (
            <Marker tone="bad" title={mot.status === "Failed" ? "Latest MOT failed" : mot.label === "Expired" ? `MOT expired on ${formatReportDate(mot.expiry)}` : "MOT not valid"}>This vehicle should not be driven on the road until it passes an MOT.</Marker>
          ) : null}
          {mot.stats && (
            <div className="slr-stats slr-stats-4">
              <div><span>Passed first time</span><strong>{mot.stats.firstTime} of {mot.stats.cycles}</strong></div>
              <div><span>Passed on retest</span><strong>{mot.stats.retests}</strong></div>
              <div><span>Major faults</span><strong>{mot.defectCounts.major}</strong></div>
              <div><span>Dangerous faults</span><strong>{mot.defectCounts.dangerous}</strong></div>
            </div>
          )}
          {mot.cycles.map((cycle) => {
            const final = cycle.final;
            return (
              <div className="slr-card slr-mot" key={final.completed} data-pdf-block>
                <div className="slr-mot-head">
                  <Pill tone={cycle.outcome === "fail" ? "bad" : "good"} label={cycle.outcome === "fail" ? "Fail" : "Pass"} />
                  <strong>{formatReportDate(final.completed)}</strong>
                  {final.mileage != null && <span>{formatMiles(final.mileage)}</span>}
                  {final.expiry && <span className="slr-mot-expiry">Expires {formatReportDate(final.expiry)}</span>}
                </div>
                {cycle.tests.slice().reverse().map((test) => {
                  const isFinal = test === final;
                  const failedFirst = test.result === "FAILED" && !isFinal;
                  if (!test.defects?.length && !failedFirst) return null;
                  return (
                    <div className="slr-mot-block" key={`${test.completed}-${test.test_number}`}>
                      {cycle.tests.length > 1 && (
                        <h4>{failedFirst ? `Failed on ${formatReportDate(test.completed)}` : cycle.outcome === "fail" ? `Failed again on ${formatReportDate(test.completed)}` : "At the passing test"}</h4>
                      )}
                      {test.defects?.length ? (
                        <ul>
                          {test.defects.map((defect, j) => (
                            <li key={j}>
                              <span className={`slr-defect slr-defect-${String(defect.type || "").toLowerCase()}`}>{defect.type === "UNCLASSIFIED" ? "Note" : defect.type}</span>
                              <span>{defect.text}</span>
                            </li>
                          ))}
                        </ul>
                      ) : <p className="slr-muted">No faults recorded at this test.</p>}
                    </div>
                  );
                })}
                {cycle.tests.length === 1 && !final.defects?.length && <p className="slr-muted slr-mot-block">No advisories or faults recorded.</p>}
              </div>
            );
          })}
        </Section>
      )}

      {/* ---------- Road tax ---------- */}
      {(vehicle?.tax_status || details.taxAnnual) && (
        <Section id="tax" icon={CalendarClock} title="Road tax" intro="Tax status from DVLA, with current UK rates." tone={vehicle?.tax_status === "Taxed" ? "good" : vehicle?.tax_status ? "warn" : undefined} toneLabel={vehicle?.tax_status || undefined}>
          {vehicle?.tax_status && (
            <Marker tone={vehicle.tax_status === "Taxed" ? "good" : "warn"} title={vehicle.tax_status === "Taxed" ? "Taxed" : vehicle.tax_status === "SORN" ? "Declared off the road (SORN)" : "Not taxed"}>
              {vehicle.tax_due_date
                ? `${vehicle.tax_status === "Taxed" ? "Tax due" : "Tax was due"} ${formatReportDate(vehicle.tax_due_date)}.`
                : null}
            </Marker>
          )}
          {(details.taxAnnual || details.taxSixMonth) && (
            <div className="slr-stats">
              {details.taxAnnual && <div><span>12 months</span><strong>{details.taxAnnual}</strong></div>}
              {details.taxSixMonth && <div><span>6 months</span><strong>{details.taxSixMonth}</strong></div>}
            </div>
          )}
        </Section>
      )}

      {/* ---------- Service history (with-service-history package only) ---------- */}
      {report.service_history && (
        <Section id="service" icon={Wrench} title="Manufacturer service history" intro="Services and repairs recorded by the manufacturer's dealer network." tone={report.service_history.success && report.service_history.events?.length ? "good" : "muted"} toneLabel={report.service_history.success && report.service_history.events?.length ? `${report.service_history.events.length} records` : "No records"}>
          {report.service_history.success && report.service_history.events?.length ? (
            <ol className="slr-card slr-timeline">
              {report.service_history.events.map((event, i) => (
                <li className="slr-timeline-item slr-tone-good" key={i} data-pdf-block>
                  <time>{formatReportDate(event.date_of_service_event) || "Date not recorded"}</time>
                  <i aria-hidden="true" />
                  <div>
                    <strong>{event.service_provider || "Service recorded"}</strong>
                    {event.mileage_observed != null && <small>{formatMiles(event.mileage_unit === "km" ? Math.round(event.mileage_observed * 0.621371) : event.mileage_observed)}</small>}
                    {event.service_actions?.length > 0 && (
                      <ul className="slr-service-actions">
                        {event.service_actions.map((action, j) => <li key={j}>{action === "unknown." ? "Details not recorded" : action}</li>)}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <Marker tone="muted" title="No service records found">
              {report.service_history.error || "Digital records depend on the manufacturer. No result does not prove the vehicle was never serviced."}
            </Marker>
          )}
        </Section>
      )}

      {/* ---------- Keepers, logbooks and plates ---------- */}
      {(model.keepers || model.v5cDates.length > 0 || model.plates.length > 0) && (
        <Section id="ownership" icon={Users} title="Keepers, logbook and plates" intro="Registered keepers, V5C logbooks and registration changes.">
          <div className="slr-columns">
            {(model.keepers || model.v5cDates.length > 0) && (
              <div className="slr-card slr-list-card" data-pdf-block>
                {model.keepers && (
                  <>
                    <span className="slr-label">Keepers</span>
                    <h3>{model.keepers.total} {model.keepers.total === 1 ? "keeper" : "keepers"}</h3>
                    <p className="slr-muted">Includes the current keeper.</p>
                    {model.keepers.lastChange && <div className="slr-list-row"><span>Last keeper change</span><strong>{formatReportDate(model.keepers.lastChange)}</strong></div>}
                  </>
                )}
                {model.v5cDates.map((date) => (
                  <div className="slr-list-row" key={date}><span>V5C logbook issued</span><strong>{formatReportDate(date)}</strong></div>
                ))}
              </div>
            )}
            <div className="slr-card slr-list-card" data-pdf-block>
              <span className="slr-label">Registration history</span>
              <h3>{model.plates.length ? `${model.plates.length} plate ${model.plates.length === 1 ? "change" : "changes"}` : "No plate change recorded"}</h3>
              {model.plates.length === 0 && model.known && <p className="slr-muted">This vehicle has kept its registration.</p>}
              {model.plates.map((plate, i) => (
                <div className="slr-list-row" key={i}>
                  {plate.registration ? <span className="slr-mini-plate">{formatUkRegistration(plate.registration)}</span> : <span>Plate change</span>}
                  <strong>{formatReportDate(plate.date)}{plate.heldFor && <small>{plate.current ? "Current plate" : "Held"} · {plate.heldFor}</small>}</strong>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ---------- Identity ---------- */}
      <Section id="identity" icon={Fingerprint} title="VIN and vehicle identity" intro="Use these to check the vehicle and its paperwork match.">
        <div className="slr-card" data-pdf-block>
          <Facts rows={details.identityRows} />
        </div>
        {details.vin && <p className="slr-hint">Compare the VIN here with the windscreen, the stamped chassis number and the V5C. Stop if any character is different.</p>}
        {details.specGroups.filter((group) => group.rows.length).length > 0 && (
          <div className="slr-columns">
            {details.specGroups.filter((group) => group.rows.length).map((group) => (
              <div className="slr-card slr-spec" key={group.title} data-pdf-block>
                <span className="slr-label">{group.title}</span>
                {group.rows.map(([label, value]) => (
                  <div className="slr-list-row" key={label}><span>{label}</span><strong>{value}</strong></div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ---------- Vehicle status ---------- */}
      {model.scrapped && (
        <Section id="status" icon={BadgeCheck} title="Vehicle status" intro="Import, export, scrap and safety recall records." tone={model.scrapped.status || model.scrapped.exported || model.recall === true ? "bad" : model.scrapped.imported ? "warn" : "good"}>
          <div className="slr-card slr-status" data-pdf-block>
            {[
              ["Imported", model.scrapped.imported ? `Yes${model.scrapped.importNonEu ? ", from outside the EU" : ""}` : "No", model.scrapped.imported ? "warn" : "good"],
              ["Exported", model.scrapped.exported ? `Yes${model.scrapped.dateExported ? `, ${formatReportDate(model.scrapped.dateExported)}` : ""}` : "No", model.scrapped.exported ? "bad" : "good"],
              ["Scrapped", model.scrapped.status ? `Yes${model.scrapped.date ? `, ${formatReportDate(model.scrapped.date)}` : ""}` : "No", model.scrapped.status ? "bad" : "good"],
              ["Certificate of destruction", model.scrapped.certificateOfDestruction ? "Issued" : "No", model.scrapped.certificateOfDestruction ? "bad" : "good"],
              model.vic ? ["VIC inspection", `${model.vic.count} recorded`, "warn"] : null,
              model.recall !== null ? ["Safety recall", model.recall ? "Outstanding" : "None outstanding", model.recall ? "bad" : "good"] : null,
            ].filter(Boolean).map(([label, value, tone]) => (
              <div className="slr-status-row" key={label}>
                <span>{label}</span>
                <strong className={`slr-text-${tone}`}>{tone === "good" ? "✓ " : ""}{value}</strong>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ---------- Valuation ---------- */}
      {report.valuation && (
        <Section id="valuation" icon={Tag} title="Vehicle valuation" intro={details.valuationMileage ? `Today's market prices at about ${new Intl.NumberFormat("en-GB").format(details.valuationMileage)} miles.` : "Today's market prices for this vehicle."}>
          {details.valuationRows.length ? (
            <>
              <div className="slr-valuation" data-pdf-block>
                {details.valuationRows.map(([label, value]) => (
                  <div className={label === "Private sale" ? "slr-valuation-main" : ""} key={label}>
                    <span>{label}</span>
                    <strong>{details.formatValuation(value)}</strong>
                  </div>
                ))}
              </div>
              <p className="slr-hint">A guide only. Condition, service history and demand change what it sells for.</p>
            </>
          ) : (
            <Marker tone="muted" title="No valuation for this vehicle">We couldn't get a live valuation for this vehicle.</Marker>
          )}
        </Section>
      )}

      <footer className="slr-footer" data-pdf-block>
        <div className="slr-footer-checks">
          {report.checks_ordered?.map((item, i) => (
            <span key={i}><CheckCircle2 size={16} color={INK} aria-hidden="true" /> {`${item.label} · £${(item.price_pence / 100).toFixed(2)}`}</span>
          ))}
        </div>
        <p>Report for {displayReg} by ServiceLad. Records come from DVLA, DVSA and UK insurance and finance registers via trusted data partners. ServiceLad is independent and not part of DVLA or DVSA. Records can change after this report was made. The data in this report is not a guide to buy or sell this vehicle.</p>
      </footer>
    </div>
  );
}
